import React, { PureComponent } from 'react';
import { CSSTransition } from 'react-transition-group';
import { CALL_STATE_TRANSITION_DURATION } from '../../constants/appRuntime';
import CardWrapper from '../../components/Trainer/styled/CardWrapper';
import CallRatios from '../../components/CallRatios/CallRatios';
import NoCallMessage from '../../components/common/NoCallMessage/NoCallMessage';
import NewCallMessage from '../../components/common/NewCallMessage/NewCallMessage';
import CallEndMessage from '../../components/common/CallEndMessage/CallEndMessage';
import CallContinuedMessage from '../../components/common/CallContinuedMessage/CallContinuedMessage';
import { getUserInterfaceConfig, isExplicitMode } from '../../store/selectors/trainer';
import { DISPLAY_NEW_SPEECH_RATE, isFeatureEnabled } from '../../config/features';
import useLocalization from '../../localization/useLocalization';
import { useAppSelector } from '../../store/hooks';
import type { ComponentType } from 'react';
import type { RootState } from '../../store/types';

type LocalizationOptions = Record<string, string | number | boolean | null | undefined>;

interface NewCallMessagePropsCompat {
    displayCallOptOutControls: boolean;
    localization: { getText: (key: string, options?: LocalizationOptions) => string };
}
const NewCallMessageCompat = NewCallMessage as ComponentType<NewCallMessagePropsCompat>;

interface CallInformationProps {
    speechRate?: number | null;
    newSpeechRate?: number | null;
    speechRatio?: number | null;
    speechPace?: number | null;
    loudness?: number | null;
    showNewCallMessage: boolean;
    showCallEndMessage: boolean;
    localization: { getText: (key: string, options?: LocalizationOptions) => string };
    isCallStarted: boolean;
    isSoundContinued: boolean;
    isExplicitModeOn: boolean;
    displayCallOptOutControls: boolean;
    displayNewSpeechRate: boolean;
}

class CallInformation extends PureComponent<CallInformationProps> {
    static defaultProps: Partial<CallInformationProps> = {
        speechRate: null,
        newSpeechRate: null,
        speechRatio: null,
        speechPace: null,
        loudness: null
    };

    _renderNewCallMessage = () => {
        const { localization, showNewCallMessage, displayCallOptOutControls } = this.props;
        if (displayCallOptOutControls) return;

        return (
            <CSSTransition in={showNewCallMessage} classNames="message" timeout={CALL_STATE_TRANSITION_DURATION} mountOnEnter unmountOnExit>
                {() =>
                    showNewCallMessage ? (
                        <NewCallMessageCompat displayCallOptOutControls={displayCallOptOutControls} localization={localization} />
                    ) : null
                }
            </CSSTransition>
        );
    };

    _renderCallEndMessage = () => {
        const { localization, showCallEndMessage, isExplicitModeOn } = this.props;
        if (!isExplicitModeOn) {
            return null;
        }

        return (
            <CSSTransition in={showCallEndMessage} classNames="message" timeout={CALL_STATE_TRANSITION_DURATION} mountOnEnter unmountOnExit>
                {() => (showCallEndMessage ? <CallEndMessage localization={localization} /> : null)}
            </CSSTransition>
        );
    };

    _renderCallContinuedMessage = () => {
        const { localization, isSoundContinued } = this.props;
        return (
            <CSSTransition in={isSoundContinued} classNames="message" timeout={CALL_STATE_TRANSITION_DURATION} mountOnEnter unmountOnExit>
                {() => <CallContinuedMessage localization={localization} />}
            </CSSTransition>
        );
    };

    _renderNoCallMessage = () => {
        const { isCallStarted, localization } = this.props;

        if (!isCallStarted) {
            return <NoCallMessage localization={localization} />;
        }
    };

    _renderCallRatios = () => {
        const { loudness, speechRate, newSpeechRate, speechRatio, localization, speechPace, showCallEndMessage, displayNewSpeechRate } =
            this.props;

        return (
            <CallRatios
                callEnded={showCallEndMessage}
                disabled={false}
                loudness={loudness}
                speechRate={speechRate}
                newSpeechRate={newSpeechRate}
                speechRatio={speechRatio}
                speechPace={speechPace}
                localization={localization}
                displayNewSpeechRate={displayNewSpeechRate}
            />
        );
    };

    render() {
        return (
            <CardWrapper data-testid="trainer-call-information-card" style={{ position: 'relative', width: 300 }}>
                {this._renderNoCallMessage()}
                {this._renderNewCallMessage()}
                {this._renderCallEndMessage()}
                {this._renderCallContinuedMessage()}
                {this._renderCallRatios()}
            </CardWrapper>
        );
    }
}

function CallInformationContainer() {
    const localization = useLocalization();
    const stateProps = useAppSelector((state: RootState) => ({
        speechRate: state.trainer.speechRate,
        newSpeechRate: state.trainer.newSpeechRate,
        speechPace: state.trainer.speechPace,
        speechRatio: state.trainer.speechRatio,
        loudness: state.trainer.loudness,
        showNewCallMessage: state.trainer.showNewCallMessage,
        showCallEndMessage: state.trainer.showCallEndMessage,
        isSoundContinued: state.trainer.isSoundContinued,
        isCallStarted: state.trainer.isStarted,
        displayNewSpeechRate: Boolean(isFeatureEnabled(getUserInterfaceConfig(state), DISPLAY_NEW_SPEECH_RATE)),
        displayCallOptOutControls: Boolean(state.trainer.userInterfaceConfig.displayCallOptOutControls),
        isExplicitModeOn: Boolean(isExplicitMode(state))
    }));

    return <CallInformation {...stateProps} localization={localization} />;
}

export default CallInformationContainer;
